import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { compressImageFile } from "@/lib/imageCompress";
import { useLang } from "@/contexts/LangContext";
import { Download, Trash, Upload, FilePdf, Image as ImageIcon, File as FileIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";

function fileIcon(ct = "") {
  if (ct.startsWith("image/")) return <ImageIcon size={18} className="text-pf-primary" />;
  if (ct === "application/pdf") return <FilePdf size={18} className="text-pf-danger" />;
  return <FileIcon size={18} className="text-pf-text-secondary" />;
}

export default function ConsultationAttachments({ consultationId, attachments = [], onChange }) {
  const { t } = useLang();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(null); // { url, filename } for image modal

  async function uploadFiles(files) {
    if (!files || files.length === 0) return;
    setBusy(true);
    let updated = attachments.slice();
    for (const file of files) {
      const fd = new FormData();
      try {
        // Downscale images client-side first — keeps uploads small/fast and
        // avoids proxy size limits and backend memory spikes.
        fd.append("file", await compressImageFile(file));
        const { data } = await api.post(`/consultations/${consultationId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        updated = [...updated, data];
      } catch (err) {
        // Graceful per-file failure: show a clear, retryable message and keep
        // the user in the consultation with all other data intact.
        toast.error(`${file.name}: ${formatApiError(err)}`);
      }
    }
    setBusy(false);
    onChange && onChange(updated);
    if (inputRef.current) inputRef.current.value = "";
  }

  async function del(aid) {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/consultations/${consultationId}/attachments/${aid}`);
      onChange && onChange(attachments.filter((a) => a.id !== aid));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function viewAttachment(a) {
    const isImage = (a.content_type || "").startsWith("image/");
    const isPdf = a.content_type === "application/pdf";
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // Pre-open a tab synchronously for PDFs on iOS (window.open after an await
    // is blocked by Safari's popup rules).
    let iosWin = null;
    if (isIOS && isPdf) iosWin = window.open("", "_blank");
    try {
      const res = await api.get(`/consultations/${consultationId}/attachments/${a.id}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: a.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      if (isImage) {
        setPreview({ url, filename: a.filename });
        return; // URL revoked when modal closes
      }
      if (isPdf) {
        if (iosWin) iosWin.location = url;
        else window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = a.filename || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (iosWin) iosWin.close();
      toast.error(formatApiError(e));
    }
  }

  function closePreview() {
    if (preview?.url) setTimeout(() => URL.revokeObjectURL(preview.url), 500);
    setPreview(null);
  }

  function onDrop(e) {
    e.preventDefault();
    setDrag(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) uploadFiles(files);
  }

  return (
    <div className="space-y-3" data-testid="consult-attachments">
      <p className="text-xs text-pf-text-secondary">{t("consultAttachmentsHint")}</p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed cursor-pointer transition-all p-6 text-center ${
          drag ? "border-pf-primary bg-pf-primary-light" : "border-pf-border hover:bg-pf-bg"
        }`}
        data-testid="consult-attach-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept="image/*,application/pdf"
          onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
          data-testid="consult-attach-input"
        />
        <Upload size={22} className="mx-auto text-pf-primary mb-1" />
        <div className="text-sm font-medium text-pf-text">
          {busy ? t("uploading") : t("dropOrClick")}
        </div>
        <div className="text-xs text-pf-text-secondary mt-1">{t("fileTypes")}</div>
      </div>

      {attachments && attachments.length > 0 && (
        <ul className="divide-y divide-pf-border border border-pf-border rounded-lg">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-3 p-3">
              {fileIcon(a.content_type)}
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => viewAttachment(a)}
                  className="text-sm font-medium truncate text-left text-pf-primary hover:underline w-full"
                  data-testid={`consult-attach-name-${a.id}`}
                  title={t("view") || "View"}
                >
                  {a.filename}
                </button>
                <div className="text-xs text-pf-text-secondary">
                  {(a.size / 1024).toFixed(1)} KB · {a.content_type}
                </div>
              </div>
              <button
                type="button"
                onClick={() => viewAttachment(a)}
                className="pf-btn-ghost !py-1.5"
                data-testid={`consult-attach-download-${a.id}`}
                title={t("view") || "View"}
              >
                <Download size={14} />
              </button>
              <button
                type="button"
                onClick={() => del(a.id)}
                className="pf-btn-ghost !py-1.5 text-pf-danger border-pf-danger/30 hover:bg-pf-danger-bg"
                data-testid={`consult-attach-delete-${a.id}`}
              >
                <Trash size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-4xl p-2" data-testid="attach-image-modal">
          {preview && (
            <div className="flex flex-col">
              <div className="text-sm font-medium text-pf-text px-2 py-1 truncate">{preview.filename}</div>
              <img
                src={preview.url}
                alt={preview.filename}
                className="max-h-[75vh] w-auto mx-auto object-contain rounded-md"
                data-testid="attach-image-modal-img"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
